import React, { useCallback, useMemo, useState } from 'react';
import { Grid } from '@material-ui/core';
import { FormProvider, useForm } from 'react-hook-form';
import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useDispatch } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  ReactFormInput,
  SimpleButton,
  StripeInput,
} from '../../../components';
import useStyles from './styles';
import yup from '../../../utility/yup';
import BillingService from '../../../services/BillingService';
import { User } from '../../../types/items';
import { getPaymentMethodRequest } from '../../../store/modules/billing/actions';

const paymentMethodSchema = yup.object().shape({
  zip_code: yup.string().required(),
});

type Props = {
  changePaymentMethod: () => void;
  user: User;
};

const PaymentMethodForm = ({ changePaymentMethod, user }: Props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const customerFullName = user?.ref.full_name_first_last || '';
  const [validCard, setValidCard] = useState(false);
  const [validCVC, setValidCVC] = useState(false);
  const [validExpDate, setValidExpDate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState();
  const stripe = useStripe();
  const elements = useElements();
  const initialValues = {
    zip_code: '',
  };

  const form = useForm({
    defaultValues: initialValues || undefined,
    resolver: yupResolver(paymentMethodSchema),
    mode: 'onChange',
  });
  const { getValues, formState: { isValid } } = form;

  const handleSubmit = useCallback(async (submitEvent) => {
    submitEvent.preventDefault();
    setIsSubmitting(true);
    const formValues = getValues();

    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);

    if (cardElement) {
      const result = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: customerFullName,
          address: {
            postal_code: formValues.zip_code as string,
          },
        },
      });

      if (result.error) {
        console.log('[error]', result.error.message);
        return;
      }

      try {
        if (result.paymentMethod && result.paymentMethod.id) {
          await BillingService.updatePaymentMethod(user.id, result.paymentMethod.id);
          dispatch(getPaymentMethodRequest(user.id));
          changePaymentMethod();
        }
      } catch (e) {
      }
    }
    // setIsSubmitting(false);
  }, [
    stripe,
    elements,
    customerFullName,
    user,
    setIsSubmitting,
    getValues,
    changePaymentMethod,
    dispatch,
  ]);

  const isValidForm = useMemo(() => isValid && validCVC && validCard && validExpDate, [isValid, validCVC, validCard, validExpDate]);

  return (
    <FormProvider
      {...form}
    >
      <form onSubmit={handleSubmit}>
        <Grid container spacing={4}>

          <Grid item container spacing={2} xs={12}>
            <Grid item lg={3} md={3} sm={6} xs={12}>
              <StripeInput
                name="card_number"
                label="Card number"
                componentType="card"
                setValidStatus={setValidCard}
              />
            </Grid>
            <Grid item lg={2} md={2} sm={6} xs={12}>
              <StripeInput
                name="expiration_date"
                label="Expiration (MM/YY)"
                componentType="expiration_date"
                setValidStatus={setValidExpDate}
              />
            </Grid>
          </Grid>
          <Grid item container spacing={2} xs={12}>
            <Grid item lg={2} md={2} sm={6} xs={12}>
              <StripeInput
                name="cvc"
                label="CVC"
                componentType="cvc"
                setValidStatus={setValidCVC}
              />
            </Grid>
            <Grid item lg={2} md={2} sm={6} xs={12}>
              <ReactFormInput
                label="Zip Code"
                name="zip_code"
                inputProps={{
                  maxLength: 12,
                }}
                hideErrors
              />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <div className={classes.buttonPanel}>
              <SimpleButton disabled={isSubmitting || !isValidForm} onClick={handleSubmit}>Update Card</SimpleButton>
              <SimpleButton
                disabled={isSubmitting}
                colorType="dangerous"
                onClick={changePaymentMethod}
              >
                Cancel
              </SimpleButton>
            </div>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};

export default PaymentMethodForm;
